import { useCallback, useEffect, useState } from "react";
import {
  getNotificationPermission,
  NotificationPermissionState,
  recordSoftAskDeclined,
  requestNotificationPermission,
  shouldOfferNotifications,
} from "./chatNotifications";

/**
 * Drives the soft-ask: offer notifications when the user comes BACK to a tab
 * where a chat finished without them.
 *
 * The offer cannot be made at the moment of the miss — the tab is unfocused, so
 * there is no user activation to carry a permission request, and the dialog
 * would open on a tab nobody is looking at. Returning to focus is both the
 * first moment the prompt is visible and the first moment the user has felt the
 * problem it solves.
 */
export function useChatNotifications() {
  const [offering, setOffering] = useState(false);
  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    getNotificationPermission()
  );

  useEffect(() => {
    const onFocus = () => {
      if (shouldOfferNotifications()) setOffering(true);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  /** Must stay wired to a click — the browsers require the user gesture. */
  const accept = useCallback(async () => {
    setOffering(false);
    setPermission(await requestNotificationPermission());
  }, []);

  const decline = useCallback(() => {
    setOffering(false);
    recordSoftAskDeclined();
  }, []);

  return { offering, permission, accept, decline };
}
